import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { defaultCountries } from "react-international-phone";

import {
  buildLinkingPhoneCanonical,
  deriveLinkingPhoneDisplayValue,
  inferPhoneInputCountry,
  normalizeLinkingPhonePrefill,
} from "@/lib/linking-phone";
import { useI18n } from "@/lib/i18n";

const PREFERRED_COUNTRIES = ["za", "us", "gb", "au"];

const COUNTRY_OPTIONS = (() => {
  const uniqueCountries = new Map();
  for (const [name, iso2, dialCode] of defaultCountries) {
    if (!uniqueCountries.has(iso2)) {
      uniqueCountries.set(iso2, { dialCode, iso2, name });
    }
  }

  const countries = [...uniqueCountries.values()];
  countries.sort((left, right) => {
    const preferredDelta =
      Number(PREFERRED_COUNTRIES.includes(right.iso2)) - Number(PREFERRED_COUNTRIES.includes(left.iso2));
    if (preferredDelta !== 0) {
      return preferredDelta;
    }
    return left.name.localeCompare(right.name);
  });
  return countries;
})();

const COUNTRY_BY_ISO2 = new Map(COUNTRY_OPTIONS.map((country) => [country.iso2, country]));

function toFlagEmoji(countryIso2) {
  return countryIso2
    .toUpperCase()
    .split("")
    .map((letter) => String.fromCodePoint(letter.charCodeAt(0) + 127397))
    .join("");
}

export default function PhoneNumberField({
  defaultCountry,
  disabled = false,
  id,
  onChange,
  value,
}) {
  const { tx } = useI18n();
  const initialCountry = inferPhoneInputCountry(value, defaultCountry);
  const [selectedCountry, setSelectedCountry] = useState(initialCountry);
  const [inputValue, setInputValue] = useState(() => deriveLinkingPhoneDisplayValue(value, initialCountry));
  const lastEmittedCanonicalRef = useRef(normalizeLinkingPhonePrefill(value));
  const lastDefaultCountryRef = useRef(defaultCountry);

  useEffect(() => {
    const incomingCanonical = normalizeLinkingPhonePrefill(value);
    const defaultCountryChanged = defaultCountry !== lastDefaultCountryRef.current;
    if (incomingCanonical === lastEmittedCanonicalRef.current && !defaultCountryChanged) {
      return;
    }

    lastEmittedCanonicalRef.current = incomingCanonical;
    lastDefaultCountryRef.current = defaultCountry;

    if (!incomingCanonical) {
      setInputValue("");
      if (defaultCountryChanged) {
        setSelectedCountry(inferPhoneInputCountry("", defaultCountry));
      }
      return;
    }

    const nextCountry = inferPhoneInputCountry(incomingCanonical, defaultCountry);
    setSelectedCountry(nextCountry);
    setInputValue(deriveLinkingPhoneDisplayValue(incomingCanonical, nextCountry));
  }, [defaultCountry, value]);

  const selectedCountryMeta = COUNTRY_BY_ISO2.get(selectedCountry) || COUNTRY_BY_ISO2.get("za");

  const handleCountryChange = (event) => {
    const nextCountry = event.target.value;
    const nextCanonical = buildLinkingPhoneCanonical(inputValue, nextCountry);
    setSelectedCountry(nextCountry);
    lastEmittedCanonicalRef.current = normalizeLinkingPhonePrefill(nextCanonical);
    onChange(nextCanonical);
  };

  const handleInputChange = (event) => {
    const nextInputValue = event.target.value;
    const nextCountry = inferPhoneInputCountry(nextInputValue, selectedCountry);
    const nextCanonical = buildLinkingPhoneCanonical(nextInputValue, nextCountry);
    setInputValue(nextInputValue);
    if (nextCountry !== selectedCountry) {
      setSelectedCountry(nextCountry);
    }
    lastEmittedCanonicalRef.current = normalizeLinkingPhonePrefill(nextCanonical);
    onChange(nextCanonical);
  };

  return (
    <div className="mincool-phone-input flex h-12 w-full items-center overflow-hidden rounded-xl border border-input bg-background shadow-sm transition-shadow focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
      <label className="relative flex h-full shrink-0 items-center border-r border-input/80 px-3">
        <span className="pointer-events-none flex items-center gap-2 text-sm font-medium text-foreground">
          <span className="text-lg leading-none">{toFlagEmoji(selectedCountryMeta.iso2)}</span>
          <span>+{selectedCountryMeta.dialCode}</span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </span>
        <select
          aria-label={tx("Country selector")}
          className="absolute inset-0 cursor-pointer opacity-0"
          disabled={disabled}
          onChange={handleCountryChange}
          value={selectedCountryMeta.iso2}
        >
          {COUNTRY_OPTIONS.map((country) => (
            <option key={country.iso2} value={country.iso2}>
              {country.name} (+{country.dialCode})
            </option>
          ))}
        </select>
      </label>
      <input
        id={id}
        autoComplete="tel"
        className="h-12 w-full bg-transparent px-4 text-base text-foreground placeholder:text-muted-foreground focus:outline-none"
        disabled={disabled}
        inputMode="tel"
        onChange={handleInputChange}
        placeholder="82 000 0001"
        type="tel"
        value={inputValue}
      />
    </div>
  );
}
